/**
 * BadgeCard Component
 *
 * Displays a single badge with earned/locked state.
 * Uses warm, muted colors to feel encouraging without being gamified.
 */
import React from 'react';
import { View, Image, StyleSheet } from 'react-native';
import CustomText from './CustomText';
import AppStyles from '../AppStyles';
import { formatMediumDate } from '../util/format';
import { getBadgeImage, getBadgeColors, getBadgePlaceholder } from '../util/badgeUtils';
import { useTranslation } from '../i18n';

/**
 * Format timestamp to readable date
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Formatted date string
 */
const formatEarnedDate = (timestamp) => {
  if (!timestamp) return null;
  return formatMediumDate(timestamp);
};

/**
 * BadgeCard Component
 *
 * @param {Object} props
 * @param {string} props.id - Badge ID for image lookup
 * @param {string} props.name - Badge name
 * @param {string} props.subtitle - Badge subtitle/description
 * @param {string} props.color - Badge color theme (gold, blue, green, purple, teal)
 * @param {boolean} props.earned - Whether the badge has been earned
 * @param {number} props.earnedAt - Timestamp when badge was earned
 * @param {boolean} props.compact - Use compact layout (for grid display)
 * @param {boolean} props.isNext - Whether this is the next badge to earn
 * @param {string} props.progressText - Text to show under next badge (e.g., "5 more to earn")
 */
const BadgeCard = ({
  id,
  name,
  subtitle,
  color = 'gold',
  earned = false,
  earnedAt = null,
  compact = false,
  isNext = false,
  progressText = null,
}) => {
  const { t } = useTranslation('badges');
  const colors = getBadgeColors(color, earned);
  const badgeImage = getBadgeImage(id);

  // Determine variant for sizing: earned (largest), next (medium), locked (smallest)
  const getVariant = () => {
    if (earned) return 'earned';
    if (isNext) return 'next';
    return 'locked';
  };
  const variant = getVariant();

  // Compact layout: vertical with badge image as hero
  // Sizes: earned = 72px, next/locked = 64px for more uniform grid appearance
  if (compact) {
    const imageSize = variant === 'earned' ? 72 : 64;

    return (
      <View
        style={[
          styles.compactContainer,
          variant === 'locked' && styles.compactContainerLocked,
        ]}
        accessibilityRole="none"
        accessibilityLabel={t('badgeCardLabel', {
          badge: name,
          state: t(earned ? 'stateEarned' : isNext ? 'stateNextToEarn' : 'stateLocked'),
        })}
      >
        {/* Badge Image with optional ring */}
        <View style={[styles.compactImageContainer, { width: imageSize + 8, height: imageSize + 8 }]}>
          {/* Gold ring for earned badges */}
          {variant === 'earned' && (
            <View style={[styles.earnedRing, { width: imageSize + 8, height: imageSize + 8, borderRadius: (imageSize + 8) / 2 }]} />
          )}

          {/* Progress ring for next badge */}
          {variant === 'next' && (
            <View style={[styles.progressRingContainer, { width: imageSize + 8, height: imageSize + 8 }]}>
              <View style={[styles.progressRingBg, { width: imageSize + 8, height: imageSize + 8, borderRadius: (imageSize + 8) / 2 }]} />
            </View>
          )}

          {/* Show actual badge for earned/next, placeholder for locked */}
          <Image
            source={variant === 'locked' ? getBadgePlaceholder() : badgeImage}
            style={[
              { width: imageSize, height: imageSize, resizeMode: 'contain' },
              variant === 'locked' && styles.badgeImageLocked,
            ]}
          />
        </View>

        {/* Badge Name */}
        <CustomText
          font={variant === 'earned' ? 'semiBold' : 'medium'}
          size={variant === 'locked' ? 11 : 12}
          color={earned ? AppStyles.colorSet.textPrimary : isNext ? AppStyles.colorSet.textSecondary : AppStyles.colorSet.textMuted}
          numberOfLines={2}
          style={styles.compactName}
        >
          {name}
        </CustomText>

        {/* Progress text for next badge */}
        {isNext && progressText && (
          <CustomText
            font="regular"
            size={11}
            color={AppStyles.colorSet.brandPrimary}
            style={styles.progressLabel}
          >
            {progressText}
          </CustomText>
        )}
      </View>
    );
  }

  // Full layout: horizontal card style
  return (
    <View
      style={[
        styles.container,
        earned && styles.containerEarned,
        {
          backgroundColor: colors.background,
          borderColor: colors.border,
        },
      ]}
      accessibilityRole="none"
    >
      {/* Badge Icon */}
      <View
        style={styles.iconContainer}
        accessibilityRole="image"
        accessibilityLabel={`${name} badge, ${earned ? 'earned' : 'locked'}`}
      >
        {badgeImage ? (
          <Image
            source={badgeImage}
            style={[
              styles.badgeImage,
              !earned && styles.badgeImageLocked,
            ]}
          />
        ) : (
          <View
            style={[
              styles.iconCircleFallback,
              {
                backgroundColor: earned ? colors.icon : colors.background,
                borderColor: colors.border,
              },
            ]}
          >
            {!earned && <View style={[styles.lockedInner, { borderColor: colors.icon }]} />}
          </View>
        )}
      </View>

      {/* Badge Info */}
      <View style={styles.infoContainer}>
        <CustomText
          font="semiBold"
          size={15}
          color={earned ? AppStyles.colorSet.textPrimary : AppStyles.colorSet.textMuted}
          numberOfLines={1}
        >
          {name}
        </CustomText>
        <CustomText
          font="regular"
          size={12}
          color={earned ? AppStyles.colorSet.textSecondary : AppStyles.colorSet.textMuted}
          numberOfLines={1}
        >
          {subtitle}
        </CustomText>
        {earned && earnedAt && (
          <CustomText
            font="regular"
            size={11}
            color={AppStyles.colorSet.textMuted}
            style={styles.earnedDate}
          >
            {t('earnedOn', { date: formatEarnedDate(earnedAt) })}
          </CustomText>
        )}
      </View>
    </View>
  );
};


const styles = StyleSheet.create({
  // Full card layout (non-compact)
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 10,
  },
  containerEarned: {
    borderWidth: 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  iconContainer: {
    width: 56,
    height: 56,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeImage: {
    width: 56,
    height: 56,
    resizeMode: 'contain',
  },
  badgeImageLocked: {
    opacity: 0.35,
  },
  iconCircleFallback: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  lockedInner: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
  },
  infoContainer: {
    flex: 1,
  },
  earnedDate: {
    marginTop: 2,
  },

  // Compact layout (grid display) - badge-centric, minimal chrome
  compactContainer: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 4,
    marginBottom: 16,
    paddingVertical: 8,
    minWidth: 80,
  },
  compactContainerLocked: {
    opacity: 0.7,
  },
  compactImageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  compactFallback: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  compactName: {
    textAlign: 'center',
    lineHeight: 16,
  },
  progressLabel: {
    textAlign: 'center',
    marginTop: 4,
  },

  // Earned badge ring (gold glow)
  earnedRing: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: '#D4A84B',
    shadowColor: '#D4A84B',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 6,
    elevation: 4,
  },

  // Progress ring for next badge
  progressRingContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressRingBg: {
    borderWidth: 2,
    borderColor: '#E5E5E5',
    borderStyle: 'dashed',
  },
});

export default BadgeCard;
